// src/pages/SWMS.tsx — Safe Work Method Statement management
// WHS Module Revision v2.0 — Sprint Item 3
// Full create/edit modal added per Code of Practice Appendix A.
// SWMS create now available to C_WHSO (WHS Officer) as primary author.
// A04 WHS Officer Agent integrated — shows mock outputs in demo mode.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_SWMS } from '../utils/mockDataTier3'
import { formatDate } from '../utils/formatters'
import { StatusBadge } from '../components/StatusBadge'
import { AgentPanel } from '../components/AgentPanel'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchSWMS } from '../services/swmsService'
import {
  Shield, Plus, Search, ArrowLeft, X, ChevronRight,
  CheckCircle, AlertTriangle, UserCheck, Zap,
  Trash2, PlusCircle, Save, PenLine, Download,
} from 'lucide-react'
import { createToolboxDraft } from '../utils/mockDataProjectGaps'
import { markToolboxGenerated } from '../utils/workflowEvents'

// ─── Types ────────────────────────────────────────────────────────────────────
interface SWMSHazard {
  id: string
  description: string
  category: string
  likelihoodBefore: number
  consequenceBefore: number
  ratingBefore: number
  controls: string[]
  residualLikelihood: number
  residualConsequence: number
  residualRating: number
}

interface SWMSSignon {
  id: string
  employeeId: string
  employeeName: string
  role: string
  signedAt: string
}

// ─── Status config ────────────────────────────────────────────────────────────
const SWMS_STATUS: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  DRAFT:        { label: 'Draft',        bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400'  },
  UNDER_REVIEW: { label: 'Under review', bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  APPROVED:     { label: 'Approved',     bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  EXPIRED:      { label: 'Expired',      bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500'   },
  ARCHIVED:     { label: 'Archived',     bg: 'bg-gray-100',  text: 'text-gray-500',  dot: 'bg-gray-300'  },
}

// ─── Regulation 291 categories ────────────────────────────────────────────────
const REG291_CATEGORIES = [
  'Adjacent to road in use by traffic',
  'Adjacent to railway',
  'Energised electrical installation or services',
  'Energised electrical — high voltage',
  'Confined space',
  'Trench or shaft excavation deeper than 1.5 m',
  'Tunnelling',
  'Tilt-up or precast concrete',
  'Demolition',
  'Structural load-bearing elements',
  'Telecommunications tower',
  'Gas, liquid, or steam under pressure',
  'Chemical, biological, or physical contamination risk',
  'Movement of powered mobile plant',
  'Working at heights with risk of fall > 2 m',
  'Diving work',
]

const PPE_OPTIONS = [
  'Hard hat', 'Safety glasses', 'Hi-vis vest', 'Steel-capped boots',
  'Gloves (general)', 'Gloves (electrical)', 'Hearing protection',
  'Dust mask / P2 respirator', 'Full face shield', 'Safety harness',
  'Arc flash PPE', 'Rubber insulating gloves', 'Traffic controller vest',
  'Knee pads', 'Sun protection',
]

// ─── Risk level helper ────────────────────────────────────────────────────────
function riskLevel(l: number, c: number) {
  const r = l * c
  if (r >= 15) return { label: 'Extreme', cls: 'bg-red-100 text-red-800' }
  if (r >= 9)  return { label: 'High',    cls: 'bg-orange-100 text-orange-800' }
  if (r >= 4)  return { label: 'Medium',  cls: 'bg-amber-100 text-amber-800' }
  return               { label: 'Low',    cls: 'bg-green-100 text-green-700' }
}

// SWMS Printable Field Document — WHS Regulation 2011 (Qld) r.300
function printSWMS(swms: any, allSignons: any[]) {
  const now   = new Date().toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const fmtD  = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }) : '—'
  const fmtDT = (iso?: string) => iso ? new Date(iso).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'
  const rCol  = (r: number) => r >= 15 ? '#b91c1c' : r >= 9 ? '#d97706' : r >= 4 ? '#ca8a04' : '#166534'
  const rLbl  = (r: number) => r >= 15 ? 'EXTREME' : r >= 9 ? 'HIGH' : r >= 4 ? 'MEDIUM' : 'LOW'

  const hazardRows = (swms.hazards ?? []).map((h: any, i: number) => {
    const rB = typeof h.ratingBefore   === 'number' ? h.ratingBefore   : (h.likelihoodBefore   ?? 3) * (h.consequenceBefore   ?? 3)
    const rA = typeof h.residualRating === 'number' ? h.residualRating : (h.residualLikelihood ?? 2) * (h.residualConsequence ?? 2)
    const controls = Array.isArray(h.controls) ? h.controls.filter(Boolean) : []
    return `<tr style="background:${i%2===0?'#fff':'#fafafa'}">
      <td style="padding:7px 10px;font-weight:600;font-size:9.5pt;border-bottom:1px solid #e5e7eb">${h.description}<br><span style="font-size:8.5pt;color:#6b7280;font-weight:400">${h.category ?? ''}</span></td>
      <td style="padding:7px 8px;text-align:center;font-weight:bold;color:${rCol(rB)};border-bottom:1px solid #e5e7eb">${rB}<br><span style="font-size:7.5pt">${rLbl(rB)}</span></td>
      <td style="padding:7px 10px;font-size:9pt;border-bottom:1px solid #e5e7eb">${controls.map((c: string, ci: number) => `<div style="margin-bottom:3px"><span style="color:#1B3A6B;font-weight:bold">${ci+1}.</span> ${c}</div>`).join('')}</td>
      <td style="padding:7px 8px;text-align:center;font-weight:bold;color:${rCol(rA)};border-bottom:1px solid #e5e7eb">${rA}<br><span style="font-size:7.5pt">${rLbl(rA)}</span></td>
    </tr>`
  }).join('')

  const signonRows = allSignons.map((s: any) =>
    `<tr style="background:#f0fdf4"><td style="padding:7px 10px;font-size:9.5pt">${s.employeeName}</td><td style="padding:7px 8px;font-size:9pt;color:#555">${s.role}</td><td style="padding:7px 8px;font-size:9pt;color:#555">${fmtDT(s.signedAt)}</td><td style="padding:7px 8px;font-size:9.5pt;color:#166534;font-weight:bold;text-align:center">✓ Signed</td></tr>`
  ).join('')
  const blankRows = Array.from({ length: Math.max(6 - allSignons.length, 3) }, () =>
    `<tr style="height:38px"><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"><div style="border-bottom:1px solid #ccc;margin:4px 16px"></div></td></tr>`
  ).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>SWMS — ${swms.swmsNumber}</title>
<style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:19pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;font-weight:700;color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:3px;margin:14px 0 7px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:9px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-end}
.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin-bottom:12px}
.mb{border:1px solid #e5e7eb;border-radius:4px;padding:6px 10px}
.mb .l{font-size:7.5pt;color:#9ca3af;margin-bottom:1px}.mb .v{font-weight:600;font-size:9.5pt}
.sbadge{display:inline-block;padding:2px 10px;border-radius:10px;font-size:8.5pt;font-weight:700;border:1.5px solid}
.reg291{background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;padding:8px 12px;margin-bottom:10px}
.rtag{display:inline-block;background:#92400e;color:#fff;border-radius:10px;font-size:8.5pt;padding:2px 10px;margin:2px 3px 2px 0;font-weight:600}
table{width:100%;border-collapse:collapse;margin-bottom:10px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9pt}
.ppe-grid{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px}
.ppe-item{border:1.5px solid #1B3A6B;border-radius:4px;padding:3px 9px;font-size:9pt;font-weight:600;color:#1B3A6B}
.scope-box{background:#f8fafc;border-left:4px solid #1B3A6B;padding:8px 12px;margin-bottom:10px;line-height:1.7;font-size:9.5pt}
.emrg{background:#fee2e2;border:1.5px solid #fca5a5;border-radius:4px;padding:8px 12px;margin-bottom:10px;line-height:1.7;font-size:9.5pt}
.warn{background:#7f1d1d;color:#fff;padding:7px 12px;border-radius:4px;margin-bottom:12px;font-size:10pt;font-weight:700;text-align:center;letter-spacing:1px}
.so-box{border:2px solid #1B3A6B;border-radius:6px;overflow:hidden;margin-bottom:10px}
.so-hdr{background:#1B3A6B;color:#fff;padding:7px 12px;font-weight:700;font-size:10pt}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:12px;border-top:1px solid #eee;padding-top:6px}
@page{size:A4;margin:12mm}@media print{body{margin:0}}</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical Pty Ltd</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:13pt;font-weight:900;color:#1B3A6B">SAFE WORK METHOD STATEMENT</div><div style="font-size:8.5pt;color:#888">WHS Regulation 2011 (Qld) r.300</div></div>
</div>
<div class="meta">
  <div class="mb"><div class="l">SWMS number</div><div class="v">${swms.swmsNumber}</div></div>
  <div class="mb"><div class="l">Revision</div><div class="v">${swms.revision}</div></div>
  <div class="mb"><div class="l">Status</div><div class="v"><span class="sbadge" style="color:${swms.status==='APPROVED'?'#166534':'#92400e'};border-color:${swms.status==='APPROVED'?'#166534':'#d97706'}">${swms.status}</span></div></div>
  <div class="mb" style="grid-column:span 3"><div class="l">Title</div><div class="v" style="font-size:11pt">${swms.title}</div></div>
  <div class="mb"><div class="l">Job / project</div><div class="v">${swms.jobNumber}</div></div>
  <div class="mb"><div class="l">Prepared by</div><div class="v">${(swms as any).preparedBy ?? (swms as any).preparedById ?? '—'}</div></div>
  <div class="mb"><div class="l">Reviewed by</div><div class="v">${(swms as any).reviewedBy ?? '—'}</div></div>
  <div class="mb"><div class="l">Date prepared</div><div class="v">${fmtD((swms as any).preparedDate)}</div></div>
  <div class="mb"><div class="l">Printed</div><div class="v">${now}</div></div>
  <div class="mb"><div class="l">Workers signed on</div><div class="v">${allSignons.length}</div></div>
</div>
${swms.status !== 'APPROVED' ? '<div class="warn">⚠ NOT APPROVED — Workers must NOT commence work using this SWMS until formally approved.</div>' : ''}
<h2>1. Regulation 291 — High-Risk Construction Work Categories</h2>
<div class="reg291"><div style="font-size:8.5pt;color:#92400e;font-weight:bold;margin-bottom:5px">This SWMS covers the following Reg 291 categories:</div>
${(swms.highRiskActivities ?? []).map((a: string) => `<span class="rtag">${a}</span>`).join('')}
${!swms.highRiskActivities?.length ? '<span style="color:#9ca3af;font-size:9pt">No categories specified</span>' : ''}</div>
<h2>2. Scope of Work</h2>
<div class="scope-box">${(swms as any).scopeOfWork ?? 'Refer to project documentation and site-specific instructions.'}</div>
<h2>3. Hazard Register — Risk Assessment &amp; Control Measures</h2>
<table><thead><tr>
  <th style="width:28%">Hazard / risk &amp; category</th>
  <th style="width:10%;text-align:center">Risk before<br>controls</th>
  <th style="width:48%">Control measures <span style="font-weight:400;font-size:7.5pt">(Eliminate → Substitute → Isolate → Engineering → Admin → PPE)</span></th>
  <th style="width:14%;text-align:center">Residual<br>risk</th>
</tr></thead>
<tbody>${hazardRows || '<tr><td colspan="4" style="padding:10px;text-align:center;color:#9ca3af">No hazards recorded</td></tr>'}</tbody></table>
<h2>4. Personal Protective Equipment (PPE)</h2>
${(swms.ppe ?? []).length > 0 ? `<div class="ppe-grid">${(swms.ppe ?? []).map((p: string) => `<div class="ppe-item">✓ ${p}</div>`).join('')}</div>` : '<p style="font-size:9.5pt;color:#555;margin-bottom:10px">Refer to job-specific PPE matrix and site induction.</p>'}
<h2>5. Emergency Procedures</h2>
<div class="emrg"><strong style="color:#991b1b">☢ EMERGENCY: CALL 000</strong><br><br>${(swms as any).emergencyProcedures ?? 'Refer to site emergency response plan. Muster at assembly point. Contact Site Supervisor and WHSO immediately.'}</div>
<h2>6. Worker Sign-On Declaration</h2>
<div class="so-box">
  <div class="so-hdr">I declare that I have read, understood and will comply with all requirements of this SWMS before commencing work.</div>
  <table style="margin:0"><thead><tr>
    <th style="width:30%">Full name (print)</th><th style="width:25%">Role / trade</th><th style="width:25%">Date &amp; time</th><th style="width:20%;text-align:center">Signature</th>
  </tr></thead>
  <tbody>${signonRows}${blankRows}</tbody></table>
</div>
<div class="footer">MCB Civil Electrical Pty Ltd · ${swms.swmsNumber} Rev ${swms.revision} · ${swms.title} · Printed ${now}<br>Prepared per Code of Practice: How to Manage Work Health and Safety Risks (Safe Work Australia). Retain on-site for duration of works.</div>
</body></html>`

  const w = window.open('', '_blank', 'width=1060,height=900')
  if (!w) { alert('Pop-up blocked — allow pop-ups to print SWMS documents.'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 400)
}

// ─── SWMS Digital Sign-On Modal ─────────────────────────────────────────────
function SWMSSignOnModal({
  swmsNumber, onConfirm, onClose, currentUserName, currentUserRole,
}: {
  swmsNumber: string
  onConfirm: (name: string, role: string) => void
  onClose: () => void
  currentUserName: string
  currentUserRole: string
}) {
  const [name, setName]         = useState(currentUserName)
  const [role, setRole]         = useState(currentUserRole)
  const [declared, setDeclared] = useState(false)
  const now = new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-amber-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <PenLine className="w-4 h-4 text-amber-600" />Sign on to SWMS
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{swmsNumber}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 leading-relaxed">
            <p className="font-semibold mb-1">Worker declaration:</p>
            <p>
              I confirm that I have read and understood this Safe Work Method Statement, that I understand
              the hazards and control measures described, and that I will follow the work methods specified
              for the duration of this task. I understand that I must not proceed with the work if conditions
              change such that this SWMS no longer applies, and that I must report any new hazards immediately.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Full name <span className="text-red-500">*</span></label>
            <input className="input text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Role on site</label>
            <input className="input text-sm" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Electrical technician, TC, Leading hand" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={declared} onChange={e => setDeclared(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-600 flex-shrink-0" />
            <span className="text-xs text-gray-700">
              I have read and understood this SWMS and I agree to work in accordance with it.<br />
              <strong className="text-gray-900">Time recorded: {now}</strong>
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button
              onClick={() => name && declared && onConfirm(name, role)}
              disabled={!name || !declared}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              <PenLine className="w-3.5 h-3.5" />Sign on
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────
export function SWMS() {
  const { user, isTier } = useAuthStore()
  const isPM    = isTier('B_PM')
  const isAdmin = isTier('A_DEVELOPER')
  const isWHSO  = isTier('C_WHSO')          // WHS Officer — primary SWMS author
  const canCreate = isPM || isAdmin || isWHSO

  const queryClient = useQueryClient()

  const [search,      setSearch]      = useState('')
  const [selected,    setSelected]    = useState<(typeof MOCK_SWMS)[0] | null>(null)
  const [showNew,     setShowNew]     = useState(false)
  const [newStep,     setNewStep]     = useState(1)
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set())
  const [approvalMsg, setApprovalMsg] = useState<string | null>(null)
  // Field digital sign-on
  const [localSignons, setLocalSignons] = useState<Record<string, SWMSSignon[]>>({})
  const [showSignOn, setShowSignOn]     = useState(false)

  // Live SWMS from Neon — merged with mock fallback by ID
  const { data: apiSWMS = [], isLoading: swmsLoading, isError: swmsError } = useQuery({
    queryKey: ['swms'],
    queryFn:  () => fetchSWMS(),
    staleTime: 60_000,
  })
  const apiIds = new Set(apiSWMS.map(s => s.id))
  const allSWMS = [
    ...(apiSWMS as unknown as typeof MOCK_SWMS),
    ...MOCK_SWMS.filter(s => !apiIds.has(s.id)),
  ]

  const filtered = allSWMS.filter(s =>
    s.swmsNumber.toLowerCase().includes(search.toLowerCase()) ||
    s.title.toLowerCase().includes(search.toLowerCase()) ||
    s.jobNumber.toLowerCase().includes(search.toLowerCase())
  )

  // ── New SWMS form state ───────────────────────────────────────────────────
  const [newForm, setNewForm] = useState({
    jobId:               '',
    title:               '',
    revision:            'A',
    preparedBy:          user?.fullName ?? '',
    scopeOfWork:         '',
    highRiskCategories:  [] as string[],
    hazards: [{
      id:                   'h1',
      description:          '',
      category:             '',
      likelihoodBefore:     3,
      consequenceBefore:    3,
      controls:             [''],
      residualLikelihood:   2,
      residualConsequence:  2,
    }] as Array<{
      id: string; description: string; category: string;
      likelihoodBefore: number; consequenceBefore: number;
      controls: string[];
      residualLikelihood: number; residualConsequence: number;
    }>,
    ppe:                 [] as string[],
    emergencyProcedures: '',
  })
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const createMutation = useMutation({
    mutationFn: async (payload: typeof newForm) => {
      const res = await fetch(
        (import.meta.env.VITE_API_URL ?? 'http://localhost:8787') + '/api/whs/swms',
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId:    payload.jobId || 'j1',
            title:    payload.title,
            revision: payload.revision,
            scope:    payload.scopeOfWork,
            hazards:  payload.hazards.map(h => ({
              description:         h.description,
              category:            h.category,
              likelihoodBefore:    h.likelihoodBefore,
              consequenceBefore:   h.consequenceBefore,
              ratingBefore:        h.likelihoodBefore * h.consequenceBefore,
              controls:            h.controls.filter(Boolean),
              residualLikelihood:  h.residualLikelihood,
              residualConsequence: h.residualConsequence,
              residualRating:      h.residualLikelihood * h.residualConsequence,
            })),
            ppe: payload.ppe,
          }),
        }
      )
      if (!res.ok) throw new Error(await res.text())
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swms'] })
      setSubmitSuccess(true)
      setTimeout(() => {
        setShowNew(false)
        setSubmitSuccess(false)
        setNewStep(1)
        setNewForm(prev => ({ ...prev, title: '', scopeOfWork: '', highRiskCategories: [], hazards: [{ id: 'h1', description: '', category: '', likelihoodBefore: 3, consequenceBefore: 3, controls: [''], residualLikelihood: 2, residualConsequence: 2 }], ppe: [], emergencyProcedures: '' }))
      }, 1800)
    },
    onError: (e: Error) => setSubmitError(e.message),
  })

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selected) {
    const effectiveStatus = approvedIds.has(selected.id) ? 'APPROVED' : selected.status
    const ss = SWMS_STATUS[effectiveStatus] ?? SWMS_STATUS.DRAFT
    return (
      <div className="space-y-5">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="w-4 h-4" />Back to SWMS register
        </button>

        {approvalMsg && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center justify-between gap-3">
            <span>{approvalMsg}</span>
            <button onClick={() => setApprovalMsg(null)} className="text-green-500 hover:text-green-700 text-lg leading-none flex-shrink-0">×</button>
          </div>
        )}

        <AgentPanel
          key={`whs-${selected.id}`}
          agentCode="whs-officer"
          modulePage="swms"
          contextData={{
            swmsNumber:          selected.swmsNumber,
            title:               selected.title,
            status:              selected.status,
            jobNumber:           selected.jobNumber,
            revision:            selected.revision,
            preparedBy:          (selected as any).preparedBy ?? (selected as any).preparedById,
            reviewedBy:          (selected as any).reviewedBy,
            scopeOfWork:         (selected as any).scopeOfWork,
            highRiskActivities:  selected.highRiskActivities,
            hazards:             selected.hazards?.map((h: SWMSHazard) => ({
              description: h.description,
              category:    h.category,
              riskBefore:  `L${h.likelihoodBefore} x C${h.consequenceBefore} = R${h.ratingBefore}`,
              controls:    h.controls,
              riskAfter:   `L${h.residualLikelihood} x C${h.residualConsequence} = R${h.residualRating}`,
            })),
            workerSignons: selected.signons?.length ?? 0,
          }}
          platformDate={new Date().toISOString().split('T')[0]}
        />

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-gray-900">{selected.swmsNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{selected.jobNumber}</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Rev {selected.revision}</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{selected.title}</h2>
              <div className="text-xs text-gray-400 mt-1">
                Prepared by {(selected as any).preparedBy ?? ''} · {formatDate((selected as any).preparedDate)} · Reviewed by {(selected as any).reviewedBy ?? ''}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {(isPM || isAdmin) && selected.status === 'UNDER_REVIEW' && !approvedIds.has(selected.id) && (
                <button
                  onClick={() => {
                    setApprovedIds(prev => new Set([...prev, selected.id]))
                    markToolboxGenerated(selected.id)
                    try {
                      createToolboxDraft({
                        swmsId:        selected.id,
                        swmsNumber:    selected.swmsNumber,
                        jobNumber:     selected.jobNumber,
                        title:         'Toolbox Talk — ' + selected.title,
                        createdByName: user?.fullName ?? 'PM',
                      } as any)
                    } catch { /* stub — function shape may not yet accept this */ }
                    setApprovalMsg(
                      'SWMS ' + selected.swmsNumber + ' approved. Toolbox Talk draft created automatically — go to Toolbox module to finalise attendance and sign-off.'
                    )
                  }}
                  className="btn-primary text-xs px-3 py-1.5"
                >
                  Approve SWMS
                </button>
              )}
              {(isPM || isAdmin) && approvedIds.has(selected.id) && (
                <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />Approved — Toolbox Talk created
                </div>
              )}
              <button
                onClick={() => {
                  const allSignons = [...(selected.signons ?? []), ...(localSignons[selected.id] ?? [])]
                  printSWMS(selected, allSignons)
                }}
                className="btn text-xs px-3 py-1.5 flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />Print / PDF
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Scope of work</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{(selected as any).scopeOfWork}</p>
            </div>

            {selected.highRiskActivities?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  High risk activities
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selected.highRiskActivities.map((a: string, i: number) => (
                    <span key={i} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-lg">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {selected.hazards?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Hazard register</h3>
                <div className="space-y-3">
                  {selected.hazards.map((h: SWMSHazard) => (
                    <div key={h.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="font-medium text-sm text-gray-900">{h.description}</div>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">{h.category}</span>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500 mb-2">
                        <span>Before: L{h.likelihoodBefore} × C{h.consequenceBefore} = <strong className="text-red-600">R{h.ratingBefore}</strong></span>
                        <span>After: L{h.residualLikelihood} × C{h.residualConsequence} = <strong className="text-green-600">R{h.residualRating}</strong></span>
                      </div>
                      <div className="space-y-1">
                        {h.controls.map((c: string, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                            {c}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              const effectiveSignons = [
                ...(selected.signons ?? []),
                ...(localSignons[selected.id] ?? []),
              ]
              const alreadySigned = effectiveSignons.some(s => s.employeeName === (user?.fullName ?? ''))
              const effectiveStatus = approvedIds.has(selected.id) ? 'APPROVED' : selected.status
              const canSignOn = effectiveStatus === 'APPROVED' && !alreadySigned
              return (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-500" />
                    Worker sign-on ({effectiveSignons.length})
                    {alreadySigned && (
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />You are signed on
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {effectiveSignons.map((s: SWMSSignon) => (
                      <div key={s.id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {s.employeeName[0]}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">{s.employeeName}</div>
                          <div className="text-[10px] text-gray-500">{s.role}</div>
                          <div className="text-[10px] text-gray-400">{s.signedAt ? new Date(s.signedAt).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}</div>
                        </div>
                      </div>
                    ))}
                    {effectiveSignons.length === 0 && (
                      <div className="col-span-2 text-xs text-gray-400 italic">No workers signed on yet.</div>
                    )}
                  </div>
                  {canSignOn && (
                    <button onClick={() => setShowSignOn(true)}
                      className="flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-xl font-medium transition-colors">
                      <PenLine className="w-4 h-4" />Sign on to this SWMS
                    </button>
                  )}
                  {effectiveStatus !== 'APPROVED' && (
                    <p className="text-xs text-gray-400">SWMS must be approved before workers can sign on.</p>
                  )}
                </div>
              )
            })()}
          </div>
        </div>

      {/* Digital sign-on modal */}
      {showSignOn && (
        <SWMSSignOnModal
          swmsNumber={selected.swmsNumber}
          currentUserName={user?.fullName ?? ''}
          currentUserRole={
            user?.tier === 'D_FIELD' ? 'Field technician'
            : user?.tier === 'C_WHSO' ? 'WHS Officer'
            : 'Site worker'
          }
          onConfirm={(name, role) => {
            const newSignon: SWMSSignon = {
              id:           `so_${Date.now()}`,
              employeeId:   user?.id ?? 'unknown',
              employeeName: name,
              role,
              signedAt:     new Date().toISOString(),
            }
            setLocalSignons(prev => ({
              ...prev,
              [selected.id]: [...(prev[selected.id] ?? []), newSignon],
            }))
            setShowSignOn(false)
          }}
          onClose={() => setShowSignOn(false)}
        />
      )}
      </div>
    )
  }

  // ── Create SWMS — multi-step form ─────────────────────────────────────────
  if (showNew) {
    const STEPS = ['Document info', 'Scope & activities', 'Hazard register', 'PPE & emergency', 'Review & save']
    const f = newForm
    const setF = (patch: Partial<typeof newForm>) => setNewForm(prev => ({ ...prev, ...patch }))

    const addHazard = () => setF({
      hazards: [...f.hazards, {
        id: 'h' + Date.now(), description: '', category: '',
        likelihoodBefore: 3, consequenceBefore: 3, controls: [''],
        residualLikelihood: 2, residualConsequence: 2,
      }]
    })

    const updateHazard = (idx: number, patch: Partial<typeof f.hazards[0]>) =>
      setF({ hazards: f.hazards.map((h, i) => i === idx ? { ...h, ...patch } : h) })

    const removeHazard = (idx: number) =>
      setF({ hazards: f.hazards.filter((_, i) => i !== idx) })

    const addControl = (hIdx: number) =>
      updateHazard(hIdx, { controls: [...f.hazards[hIdx].controls, ''] })

    const updateControl = (hIdx: number, cIdx: number, val: string) =>
      updateHazard(hIdx, { controls: f.hazards[hIdx].controls.map((c, i) => i === cIdx ? val : c) })

    const removeControl = (hIdx: number, cIdx: number) =>
      updateHazard(hIdx, { controls: f.hazards[hIdx].controls.filter((_, i) => i !== cIdx) })

    const toggleCat = (cat: string) => setF({
      highRiskCategories: f.highRiskCategories.includes(cat)
        ? f.highRiskCategories.filter(c => c !== cat)
        : [...f.highRiskCategories, cat]
    })

    const togglePPE = (p: string) => setF({
      ppe: f.ppe.includes(p) ? f.ppe.filter(x => x !== p) : [...f.ppe, p]
    })

    const canNext = () => {
      if (newStep === 1) return !!f.title.trim() && !!f.preparedBy.trim()
      if (newStep === 2) return !!f.scopeOfWork.trim() && f.highRiskCategories.length > 0
      if (newStep === 3) return f.hazards.length > 0 && f.hazards.every(h => h.description.trim() && h.controls.some(c => c.trim()))
      return true
    }

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => { setShowNew(false); setNewStep(1) }} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4" />Cancel
          </button>
          <h1 className="text-xl font-bold text-gray-900">New SWMS</h1>
          <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full font-medium">Draft</span>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 flex-wrap gap-y-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
                i + 1 === newStep ? 'bg-blue-600 text-white' :
                i + 1 < newStep  ? 'bg-green-100 text-green-700' :
                                   'bg-gray-100 text-gray-400'
              }`}>
                {i + 1 < newStep ? <CheckCircle className="w-3 h-3" /> : <span>{i + 1}</span>}
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-5 h-px mx-1 ${i + 1 < newStep ? 'bg-green-400' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">

          {/* Step 1 — Document info */}
          {newStep === 1 && (
            <div className="space-y-4">
              <h2 className="font-semibold text-gray-900 mb-4">Document information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">SWMS title *</label>
                  <input value={f.title} onChange={e => setF({ title: e.target.value })} className="input w-full"
                    placeholder="e.g. Cable pulling in conduit — Acacia Ridge" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job / project</label>
                  <select value={f.jobId} onChange={e => setF({ jobId: e.target.value })} className="input w-full">
                    <option value="">Select job…</option>
                    <option value="j1">J1 — Acacia Ridge ITS Upgrade</option>
                    <option value="j2">J2 — Ipswich Motorway Signals</option>
                    <option value="j3">J3 — Gold Coast Light Rail Stage 4</option>
                    <option value="j4">J4 — Bruce Highway Smart Motorway</option>
                    <option value="j5">J5 — Gympie Road Corridor</option>
                    <option value="j6">J6 — Toowoomba Range Tunnel ITS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
                  <input value={f.revision} onChange={e => setF({ revision: e.target.value })} className="input w-full" placeholder="A" maxLength={3} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prepared by *</label>
                  <input value={f.preparedBy} onChange={e => setF({ preparedBy: e.target.value })} className="input w-full" />
                </div>
              </div>
              <p className="text-xs text-gray-400">SWMS document number will be auto-generated on save (SWMS-YYYY-NNN format).</p>
            </div>
          )}

          {/* Step 2 — Scope and Reg 291 categories */}
          {newStep === 2 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-gray-900">Scope of work &amp; Regulation 291 categories</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope of work *</label>
                <textarea
                  value={f.scopeOfWork}
                  onChange={e => setF({ scopeOfWork: e.target.value })}
                  className="input w-full h-28 resize-y"
                  placeholder="Describe all work activities covered by this SWMS — include location, plant, materials and work sequence…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  High-risk construction work categories (Reg 291) *
                </label>
                <p className="text-xs text-gray-400 mb-3">Select all categories that apply. At least one required.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {REG291_CATEGORIES.map(cat => (
                    <label key={cat} className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                      f.highRiskCategories.includes(cat)
                        ? 'bg-amber-50 border-amber-300'
                        : 'bg-gray-50 border-gray-200 hover:border-amber-200'
                    }`}>
                      <input type="checkbox" checked={f.highRiskCategories.includes(cat)} onChange={() => toggleCat(cat)} className="mt-0.5 accent-amber-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{cat}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Hazard register */}
          {newStep === 3 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">Hazard register</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Rate risk before controls → list controls (hierarchy order) → rate residual risk.</p>
                </div>
                <button onClick={addHazard} className="btn flex items-center gap-1.5 text-sm px-3 py-1.5">
                  <PlusCircle className="w-4 h-4" />Add hazard
                </button>
              </div>

              {f.hazards.map((h, hIdx) => {
                const before = riskLevel(h.likelihoodBefore, h.consequenceBefore)
                const after  = riskLevel(h.residualLikelihood, h.residualConsequence)
                return (
                  <div key={h.id} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hazard {hIdx + 1}</span>
                      {f.hazards.length > 1 && (
                        <button onClick={() => removeHazard(hIdx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Hazard description *</label>
                        <input value={h.description} onChange={e => updateHazard(hIdx, { description: e.target.value })} className="input w-full text-sm" placeholder="e.g. Struck by vehicle…" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                        <input value={h.category} onChange={e => updateHazard(hIdx, { category: e.target.value })} className="input w-full text-sm" placeholder="e.g. Traffic management" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-gray-600">Risk BEFORE controls</p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Likelihood (1–5)</label>
                            <input type="number" min={1} max={5} value={h.likelihoodBefore} onChange={e => updateHazard(hIdx, { likelihoodBefore: +e.target.value })} className="input w-full text-center text-sm" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Consequence (1–5)</label>
                            <input type="number" min={1} max={5} value={h.consequenceBefore} onChange={e => updateHazard(hIdx, { consequenceBefore: +e.target.value })} className="input w-full text-center text-sm" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Rating</label>
                            <div className={`rounded-lg px-1 py-1.5 text-center text-xs font-bold ${before.cls}`}>
                              {h.likelihoodBefore * h.consequenceBefore}<br /><span className="font-normal">{before.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-gray-600">Risk AFTER controls (residual)</p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Likelihood (1–5)</label>
                            <input type="number" min={1} max={5} value={h.residualLikelihood} onChange={e => updateHazard(hIdx, { residualLikelihood: +e.target.value })} className="input w-full text-center text-sm" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Consequence (1–5)</label>
                            <input type="number" min={1} max={5} value={h.residualConsequence} onChange={e => updateHazard(hIdx, { residualConsequence: +e.target.value })} className="input w-full text-center text-sm" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Rating</label>
                            <div className={`rounded-lg px-1 py-1.5 text-center text-xs font-bold ${after.cls}`}>
                              {h.residualLikelihood * h.residualConsequence}<br /><span className="font-normal">{after.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Control measures * <span className="text-gray-400 font-normal">(hierarchy: Eliminate → Substitute → Isolate → Engineering → Admin → PPE)</span>
                      </label>
                      <div className="space-y-1.5">
                        {h.controls.map((ctrl, cIdx) => (
                          <div key={cIdx} className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-4 flex-shrink-0">{cIdx + 1}.</span>
                            <input value={ctrl} onChange={e => updateControl(hIdx, cIdx, e.target.value)} className="input flex-1 text-sm" placeholder="Describe control measure…" />
                            {h.controls.length > 1 && (
                              <button onClick={() => removeControl(hIdx, cIdx)} className="text-gray-300 hover:text-red-400 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                            )}
                          </div>
                        ))}
                        <button onClick={() => addControl(hIdx)} className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1 mt-1 ml-6">
                          <PlusCircle className="w-3 h-3" />Add control
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Step 4 — PPE and emergency */}
          {newStep === 4 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-gray-900">PPE requirements &amp; emergency procedures</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required PPE</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PPE_OPTIONS.map(p => (
                    <label key={p} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${
                      f.ppe.includes(p) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-gray-50 border-gray-200 hover:border-blue-200'
                    }`}>
                      <input type="checkbox" checked={f.ppe.includes(p)} onChange={() => togglePPE(p)} className="accent-blue-600 flex-shrink-0" />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency procedures</label>
                <textarea
                  value={f.emergencyProcedures}
                  onChange={e => setF({ emergencyProcedures: e.target.value })}
                  className="input w-full h-28 resize-y"
                  placeholder="Emergency contact numbers, assembly point, nearest hospital, first aid officer, emergency shutdown procedure…"
                />
              </div>
            </div>
          )}

          {/* Step 5 — Review and save */}
          {newStep === 5 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-gray-900">Review &amp; save as Draft</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-3">Document info</p>
                  <p><span className="text-gray-400">Title: </span><span className="text-gray-900 font-medium">{f.title}</span></p>
                  <p><span className="text-gray-400">Revision: </span><span className="text-gray-900">{f.revision}</span></p>
                  <p><span className="text-gray-400">Prepared by: </span><span className="text-gray-900">{f.preparedBy}</span></p>
                  <p><span className="text-gray-400">Job: </span><span className="text-gray-900">{f.jobId || 'Not selected'}</span></p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-3">Content summary</p>
                  <p><span className="text-gray-400">Reg 291 categories: </span><span className="text-gray-900 font-medium">{f.highRiskCategories.length}</span></p>
                  <p><span className="text-gray-400">Hazards identified: </span><span className="text-gray-900 font-medium">{f.hazards.length}</span></p>
                  <p><span className="text-gray-400">Control measures: </span><span className="text-gray-900 font-medium">{f.hazards.reduce((n, h) => n + h.controls.filter(Boolean).length, 0)}</span></p>
                  <p><span className="text-gray-400">PPE items: </span><span className="text-gray-900 font-medium">{f.ppe.length}</span></p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
                <strong>Saved as Draft.</strong> The PM or Admin must approve this SWMS before work commences. Workers cannot sign on to a Draft SWMS.
              </div>
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  API save failed — in demo mode the record will appear locally after refresh. Error: {submitError}
                </div>
              )}
              {submitSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />SWMS created successfully. Returning to register…
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button onClick={() => newStep > 1 ? setNewStep(s => s - 1) : setShowNew(false)} className="btn px-5 py-2 text-sm">
            {newStep > 1 ? 'Back' : 'Cancel'}
          </button>
          {newStep < 5
            ? <button onClick={() => setNewStep(s => s + 1)} disabled={!canNext()} className="btn-primary px-5 py-2 text-sm disabled:opacity-40">Continue</button>
            : <button
                onClick={() => createMutation.mutate(f)}
                disabled={createMutation.isPending || submitSuccess}
                className="btn-primary px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
              >
                <Save className="w-4 h-4" />
                {createMutation.isPending ? 'Saving…' : 'Save as Draft'}
              </button>
          }
        </div>
      </div>
    )
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SWMS Register</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {allSWMS.length} safe work method statements
            {apiSWMS.length > 0 && <span className="ml-2 text-blue-600 font-medium">· {apiSWMS.length} live from Neon</span>}
          </p>
        </div>
        {canCreate && (
          <button onClick={() => { setNewForm(prev => ({ ...prev, preparedBy: user?.fullName ?? '' })); setShowNew(true) }}
            className="btn-primary flex items-center gap-2 text-sm px-4 py-2">
            <Plus className="w-4 h-4" />New SWMS
          </button>
        )}
      </div>

      {swmsLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Loading live SWMS from Neon…
        </div>
      )}
      {swmsError && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          ⚠ API unreachable — showing local SWMS only.
        </div>
      )}

      <AgentPanel agentCode="whs-officer" modulePage="swms" platformDate="2026-07-24" />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search SWMS number, title, or job…" className="input pl-9 w-full" />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
        {filtered.length === 0
          ? <div className="py-14 text-center text-gray-400">
              <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No SWMS found</p>
              {canCreate && <button onClick={() => setShowNew(true)} className="mt-3 btn-primary text-xs px-3 py-1.5">Create first SWMS</button>}
            </div>
          : filtered.map(s => {
              const ss = SWMS_STATUS[s.status] ?? SWMS_STATUS.DRAFT
              return (
                <div key={s.id} onClick={() => setSelected(s)} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ss.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-gray-900">{s.swmsNumber}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{s.jobNumber}</span>
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Rev {s.revision}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{s.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.highRiskActivities.length} high-risk activities · {s.signons.length} workers signed on</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                </div>
              )
            })
        }
      </div>
    </div>
  )
}
